import { Link, useLocation } from 'react-router-dom'
import { useEffect } from 'react'
import { motion } from 'motion/react'
import { Button } from '@/components/ui'
import { useSEO, createServiceSchema } from '@/hooks/useSEO'
import { useScrollReveal, useStaggeredGrid, useHeroReveal } from '@gjl/motion/css'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { Check } from 'lucide-react'
import { MANAGED_SERVICES, DFY_BUNDLES, CONSULTING } from '@gjl/voice/pricing'

// Pricing is single-sourced from @gjl/voice/pricing. Edit prices there.

const SERVICE_DETAILS: Record<string, { headline: string; points: string[] }> = {
  'website-care': {
    headline: 'One update a month, handled',
    points: [
      'One update request per month: a concert change, a new page, a season refresh',
      '72-hour turnaround',
      'Website software included in the price',
      'The step between running it yourself and Managed Website',
    ],
  },
  'managed-website': {
    headline: 'Your website, kept current without you touching it',
    points: [
      'Send an update request any time: concert dates, artist bios, new pages',
      'One active request at a time, 72-hour turnaround',
      'Design, copy edits, and technical fixes all included',
      'Website software included in the price',
    ],
  },
  'managed-email': {
    headline: 'Campaigns planned, written, designed, and sent for you',
    points: [
      'About 2 campaigns per month, plus 1 automation',
      'Season announcements, ticket launches, donor appeals, reminders',
      'List management and deliverability handled',
      'Email software included in the price',
    ],
  },
}

export default function DoneForYouPage() {
  const breadcrumbs = generateBreadcrumbs('/services/done-for-you', breadcrumbLabels)

  useSEO({
    title: 'Done-For-You Digital Services for Music Organizations',
    description:
      'Managed website and email services for music organizations, with Concierge for everything else. We operate it, you stay in charge. Software included in every plan.',
    url: '/services/done-for-you',
    canonical: '/services/done-for-you',
    breadcrumbs,
    schema: createServiceSchema({
      name: 'Done-For-You Digital Services',
      description:
        'Managed website and email marketing services for music organizations, with Concierge engagements covering fundraising and box office, underlying software included.',
      url: '/services/done-for-you',
    }),
  })

  // SPA navigation doesn't trigger native anchor scrolling (e.g. /services/done-for-you#managed-email),
  // including hash-only changes while already on this page.
  const location = useLocation()
  useEffect(() => {
    const id = location.hash.slice(1)
    if (!id) return
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
  }, [location])

  const heroRef = useHeroReveal<HTMLDivElement>(0.2)
  const servicesRef = useStaggeredGrid<HTMLDivElement>('.service-card', { stagger: 0.1 })
  const bundlesRef = useStaggeredGrid<HTMLDivElement>('.bundle-card', { stagger: 0.1 })
  const termsRef = useScrollReveal<HTMLDivElement>('.term-item', { stagger: 0.08 })

  return (
    <div className="mx-auto max-w-6xl space-y-24 px-4 py-16">
      {/* Hero */}
      <section ref={heroRef} className="mx-auto max-w-4xl space-y-6 text-center">
        <h1 className="text-fluid-h1">Done for you, not out of your hands</h1>
        <p className="text-fluid-h4 text-muted-foreground mx-auto max-w-3xl">
          Your website and your email can each be run by our team instead of yours, and Concierge covers everything,
          giving and box office included. You stay in charge; we do the work.
        </p>
        <p className="text-fluid-body text-muted mx-auto max-w-2xl">
          Each managed service includes the underlying software, so you see one number. Delegate one capability or all
          of them, and take any of them back in-house whenever you want.
        </p>
        <div className="flex flex-col items-center justify-center gap-4 pt-4 sm:flex-row">
          <a href="/book" target="_blank" rel="noopener noreferrer">
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <Button variant="default" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                Book a Free Call →
              </Button>
            </motion.div>
          </a>
          <Link to="/packages">
            <Button variant="outline" size="lg" className="cursor-pointer px-8 py-6 text-lg">
              See All Pricing
            </Button>
          </Link>
        </div>
      </section>

      {/* Managed services */}
      <section className="space-y-8">
        <h2 className="text-fluid-h2 text-center">The managed services</h2>
        <div ref={servicesRef} className="grid gap-6 md:grid-cols-2">
          {MANAGED_SERVICES.map((svc) => {
            const details = SERVICE_DETAILS[svc.id]
            return (
              <motion.div
                key={svc.id}
                id={svc.id}
                className="service-card border-border scroll-mt-24 space-y-4 rounded-lg border p-8"
                whileHover={{ y: -4 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <h3 className="text-foreground text-xl font-semibold">{svc.name}</h3>
                  <div className="text-primary text-lg font-bold whitespace-nowrap">${svc.priceMonthly}/mo</div>
                </div>
                {details && <p className="text-foreground font-medium">{details.headline}</p>}
                <ul className="space-y-2 text-sm">
                  {(details?.points ?? [svc.note]).map((point) => (
                    <li key={point} className="text-muted-foreground flex items-start gap-2">
                      <Check className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            )
          })}
        </div>
      </section>

      {/* Bundles + consulting */}
      <section className="space-y-8">
        <h2 className="text-fluid-h2 text-center">Bundles and consulting</h2>
        <div ref={bundlesRef} className="grid gap-6 md:grid-cols-2">
          {[...DFY_BUNDLES, ...CONSULTING].map((bundle) => (
            <motion.div
              key={bundle.id}
              id={bundle.id}
              className="bundle-card border-primary scroll-mt-24 space-y-3 rounded-lg border p-8"
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <div className="flex items-baseline justify-between gap-2">
                <h3 className="text-foreground text-xl font-semibold">{bundle.name}</h3>
                <div className="text-primary text-lg font-bold whitespace-nowrap">{bundle.price}</div>
              </div>
              <p className="text-muted-foreground text-sm">{bundle.note}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Terms */}
      <section className="mx-auto max-w-3xl space-y-6">
        <h2 className="text-fluid-h2 text-center">How engagements work</h2>
        <div ref={termsRef} className="space-y-4">
          {[
            'Every done-for-you plan starts with a one-time onboarding. The $250 Launch build counts, so getting a new site is also your onboarding.',
            'Plans have a 3-month minimum term. After that, month to month.',
            'Managed Website requests are unlimited: one active at a time, 72-hour turnaround.',
            'Take any capability back in-house whenever you want. The software keeps running; only the service stops.',
            'Leaving entirely? Exporting your content, contacts, and donor data is free, always.',
          ].map((term) => (
            <div key={term} className="term-item text-muted-foreground flex items-start gap-3">
              <Check className="text-primary mt-1 h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
              <span>{term}</span>
            </div>
          ))}
        </div>
        <div className="pt-6 text-center">
          <a href="/book" target="_blank" rel="noopener noreferrer">
            <Button size="lg" className="cursor-pointer">
              Book a Free Call →
            </Button>
          </a>
        </div>
      </section>
    </div>
  )
}
